import {
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  ApplicationStore,
  getModuleGroupById,
  SOFTLINE_CONFIG_OFFLINE_FEATURE,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import {
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SoftlineEntityDetailService,
  SoftlineEntityService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import {
  provideSoftAppsModule,
  withLazySoftAppsModuleTranslation,
  withSoftAppsModuleInfo,
  withSoftAppsModuleRoutes,
} from '@softapps/core';
import { default as de } from '../i18n/de.json';
import { provideWarenverprobungGlobals } from './warenverprobung.globals';
import { warenverprobungRoutes } from './warenverprobung.routes';
import { WarenverprobungResolver } from './resolver/warenverprobung-resolver.service';
import { WarenverprobungService } from './services/warenverprobung.service';
import { WarenverprobungArtikelDetailStore } from './store/warenverprobung-artikel-detail.store';
import { WarenverprobungBelegeStore } from './store/warenverprobung-belege.store';
import { WarenverprobungBeweStore } from './store/warenverprobung-bewe.store';
import { WarenverprobungStore } from './store/warenverprobung-store';
import {
  SOFTLINE_API_MDE_WARENVERPROBUNG_ARTIKEL,
  SOFTLINE_API_MDE_WARENVERPROBUNG_BEWEGUNGEN,
} from './warenverprobung.api';
import {
  SOFTLINE_FEATURE_MDE_WARENVERPROBUNG,
  SOFTLINE_FEATURE_MDE_WARENVERPROBUNG_ARTIKEL_DETAILS,
  SOFTLINE_FEATURE_MDE_WARENVERPROBUNG_BELEGE,
  SOFTLINE_FEATURE_MDE_WARENVERPROBUNG_BEWE,
} from './warenverprobung.shared';

export const warenverprobungProviders = [
  provideEnvironmentInitializer(async () => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'mdeWarenverprobung',
      language: 'de',
      translations: de,
    });

    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: 'Warenverprobung',
      icon: 'fa-regular fa-flask-vial',
      route: '/mde-warenverprobung',
      permission: 'WARENVERPROBUNG',
      group: getModuleGroupById(6),
    });

    store.commit(
      SOFTLINE_FEATURE_MDE_WARENVERPROBUNG_BEWE,
      WarenverprobungBeweStore.mutations.setSort,
      {
        property: 'id',
        title: 'Erfassungszeitpunkt',
        direction: 'desc',
      } as any
    );
  }),
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_WARENVERPROBUNG,
      feature: WarenverprobungStore.feature,
      providers: [
        {
          provide: WarenverprobungService,
          useClass: WarenverprobungService,
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_WARENVERPROBUNG_BEWE,
      feature: WarenverprobungBeweStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () =>
            new SoftlineEntityService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_MDE_WARENVERPROBUNG_BEWEGUNGEN
            ),
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_WARENVERPROBUNG_BELEGE,
      feature: WarenverprobungBelegeStore.feature,
    })
  ),
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_WARENVERPROBUNG_ARTIKEL_DETAILS,
      feature: WarenverprobungArtikelDetailStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () => {
            return new SoftlineEntityDetailService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_MDE_WARENVERPROBUNG_ARTIKEL,
              'detail'
            );
          },
        },
      ],
    })
  ),
  WarenverprobungResolver,
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: { featureName: SOFTLINE_FEATURE_MDE_WARENVERPROBUNG, fields: ['art'] },
    multi: true,
  },
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: {
      featureName: SOFTLINE_FEATURE_MDE_WARENVERPROBUNG_BEWE,
      fields: ['entities', 'ids'],
    },
    multi: true,
  },
];

export function provideWarenverprobungModule() {
  return [
    provideSoftAppsModule(
      {
        name: 'mdeWarenverprobung',
        title: 'Warenverprobung',
        icon: 'fa-regular fa-flask-vial',
        route: '/mde-warenverprobung',
        permission: 'WARENVERPROBUNG',
        group: getModuleGroupById(6),
      },
      withSoftAppsModuleRoutes({ app: warenverprobungRoutes }),
      withLazySoftAppsModuleTranslation({
        lazy: { de: () => import('../i18n/de.json') },
      }),
      withSoftAppsModuleInfo({
        library: '@softapps/mde/warenverprobung',
        remoteConfig: 'mde-warenverprobung',
        description: 'Warenverprobung mit dem MDE-Gerät.',
      })
    ),
    ...provideWarenverprobungGlobals(),
  ];
}
